import { defineStore } from 'pinia'

import Cookies from 'js-cookie'
import type { ChatWidgetSettings, LocationBranding, LocationData, LocationPermissions, LocationState } from '../types/location'

export const useLocationStore = defineStore('location', {
  state: (): LocationState => ({
    locationData: null,
    locationId: '',
    currencySymbol: '',
    currencyCode: '',
    subDomain: '',
    customDomain: '',
    languagePreferenceBuilder: '',
    branding: {
      brandColor2: null,
      brandColor1: null,
      portalBannerText: null,
      portalDescription: null,
      portalBackgroundImage: null,
      portalBannerImage: null,
      logo: null,
      portalIcon: null,
      favicon: null,
      copyRight: null,
      supportEmail: null,
    },
    chatWidgetSettings: {
      widgetId: '',
      permissions: {
        communities: true,
        membership: true,
        home: true,
        affiliate: true,
      },
    },
    locationPermissions: {
      appointmentsEnabled: true,
      membershipEnabled: true,
      affiliateManagerEnabled: true,
      communitiesEnabled: true,
      subscriptionsEnabled: true,
      contractsEnabled: true,
      estimatesEnabled: true,
      sharedFilesEnabled: true,
      certificatesEnabled: true,
    },
    customCode: null,
    isLoading: false,
    error: null,
    isSSODisabled: false,
    isAuthV2Enabled: Cookies.get('catv2') ? true : false,
    brandedHeadOwnsDocument: false,
    defaultPage: undefined,
  }),

  getters: {
    hasLocation: state => !!state.locationId,
    isCommunitiesEnabled: state => state.locationPermissions.communitiesEnabled,
    isMembershipEnabled: state => state.locationPermissions.membershipEnabled,
    brandColors: state => ({
      primary: state.branding.brandColor1,
      secondary: state.branding.brandColor2,
    }),
    portalInfo: state => ({
      title: state.branding.portalBannerText,
      description: state.branding.portalDescription,
      logo: state.branding.logo,
      favicon: state.branding.favicon,
    }),
  },

  actions: {
    setLoading(loading: boolean) {
      this.isLoading = loading
    },

    setError(error: string | null) {
      this.error = error
    },

    setLocationData(data: LocationData) {
      this.locationData = data
      this.locationId = data.locationId
      this.currencySymbol = data.currencySymbol || ''
      this.currencyCode = data.currencyCode?.trim().toUpperCase() || ''
      this.subDomain = data.subDomain || ''
      this.customDomain = data.customDomain || ''
      this.languagePreferenceBuilder = data.languagePreferenceBuilder || ''
      this.isAuthV2Enabled = data.authV2Enabled || false
      this.isSSODisabled = data.isSSODisabled ?? false

      // Set branding
      this.setBranding(data.branding || {})

      // Set permissions
      if (data.locationPermissions) {
        this.setPermissions(data.locationPermissions)
      }

      // Set chat widget settings
      if (data.chatWidgetSettings) {
        this.setChatWidgetSettings(data.chatWidgetSettings)
      }

      // Set portal custom code (client portal only — may be absent on other apps)
      this.customCode = data.customCode ?? null

      this.defaultPage = data.defaultPage

      // Apply branding to document
      this.applyBranding()
    },

    setBranding(branding: Partial<LocationBranding>) {
      this.branding = {
        ...this.branding,
        ...branding,
        portalBannerText: branding.portalBannerText || this.locationData?.portalBannerText || null,
        portalDescription: branding.portalDescription || this.locationData?.portalDescription || null,
        copyRight: branding.copyRight || this.locationData?.copyRight || null,
        supportEmail: branding.supportEmail || this.locationData?.supportEmail || null,
      }
    },

    setPermissions(permissions: Partial<LocationPermissions>) {
      this.locationPermissions = {
        appointmentsEnabled: permissions.appointmentsEnabled ?? true,
        membershipEnabled: permissions.membershipEnabled ?? true,
        affiliateManagerEnabled: permissions.affiliateManagerEnabled ?? true,
        communitiesEnabled: permissions.communitiesEnabled ?? true,
        subscriptionsEnabled: permissions.subscriptionsEnabled ?? true,
        contractsEnabled: permissions.contractsEnabled ?? true,
        estimatesEnabled: permissions.estimatesEnabled ?? true,
        sharedFilesEnabled: permissions.sharedFilesEnabled ?? true,
        certificatesEnabled: permissions.certificatesEnabled ?? true,
      }
    },

    setChatWidgetSettings(settings: Partial<ChatWidgetSettings>) {
      if (settings?.widgetId) {
        this.chatWidgetSettings.widgetId = settings.widgetId
      }

      if (settings?.permissions) {
        this.chatWidgetSettings.permissions = {
          communities: settings.permissions.communities ?? true,
          membership: settings.permissions.membership ?? true,
          home: settings.permissions.home ?? true,
          affiliate: settings.permissions.affiliate ?? true,
        }
      }
    },

    applyBranding() {
      if (typeof window === 'undefined') return

      // Title and favicon writes are gated behind `brandedHeadOwnsDocument` so that
      // when a Vue-head consumer (clientportal `useBrandedHead`) owns those slots
      // we do not double-write. gokollab and meet leave the flag `false` and keep
      // this legacy imperative path.
      if (!this.brandedHeadOwnsDocument) {
        // Set favicon
        if (this.locationData?.branding?.favicon) {
          const link = (document.querySelector("link[rel*='icon']") as HTMLLinkElement) || document.createElement('link')
          link.type = 'image/x-icon'
          link.rel = 'shortcut icon'
          link.href = this.locationData.branding.favicon
          document.getElementsByTagName('head')[0].appendChild(link)
        }

        // Set document title
        if (this.locationData?.portalBannerText || this.locationData?.branding?.portalBannerText) {
          document.title = this.locationData.portalBannerText || this.locationData.branding.portalBannerText || 'Platform'
        }
      }

      // Apply brand colors as CSS variables
      if (this.branding.brandColor1) {
        document.documentElement.style.setProperty('--cp-brand-primary', this.branding.brandColor1)
        document.documentElement.style.setProperty('--brand-color-primary', this.branding.brandColor1)
        document.documentElement.style.setProperty('--cp-platform-primary', this.branding.brandColor1)
      }
      if (this.branding.brandColor2) {
        document.documentElement.style.setProperty('--cp-brand-secondary', this.branding.brandColor2)
        document.documentElement.style.setProperty('--brand-color-secondary', this.branding.brandColor2)
      }
    },

    reset() {
      this.$reset()
    },

    getHostName(): string {
      return this.customDomain || 'communities.staging.clientclub.net'
    },
  },
})
